import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import ProductGrid from '../components/ProductGrid';
// import ShowcaseList from '../components/ShowcaseList';
import { getProductById, getRelatedProducts, toggleFavoriteProduct, getFavorites } from '../utils/dataManager';

const ProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  // 放大镜状态
  const [isZooming, setIsZooming] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: null, y: null });
  const [showOriginalPopup, setShowOriginalPopup] = useState(false);
  const imageRef = useRef(null);

  // 移除：旧的新窗口打开原图函数

  // 确保放大镜效果正确初始化
  useEffect(() => {
    // 初始化时重置放大状态
    setIsZooming(false);
    setZoomPosition({ x: 50, y: 50 });
    
    // 添加调试日志
    console.log('放大镜组件已初始化');
    
    return () => {
      // 组件卸载时清理
      setIsZooming(false);
    };
  }, [currentImageIndex]);

  // 鼠标进入图片区域时，显示放大镜
  const handleMouseEnter = (e) => {
    // 先计算初始位置，避免放大镜突然出现在默认位置
    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      
      // 确保值在0-100之间
      const boundedX = Math.max(0, Math.min(100, x));
      const boundedY = Math.max(0, Math.min(100, y));
      
      setZoomPosition({ x: boundedX, y: boundedY });
    }
    
    // 显示放大镜
    setIsZooming(true);
  };
  
  // 鼠标离开图片区域时，隐藏放大镜
  const handleMouseLeave = () => {
    setIsZooming(false);
    
    // 重置放大位置为null，以便下次进入时重新计算
    setTimeout(() => {
      setZoomPosition({ x: null, y: null });
    }, 300); // 延迟重置，与过渡动画时间匹配
  };
  
  // 处理鼠标移动计算放大位置
  const handleMouseMove = (e) => {
    if (!imageRef.current) return;
    
    // 获取图片元素的边界矩形
    const rect = imageRef.current.getBoundingClientRect();
    
    // 计算鼠标在图片上的相对位置（百分比）
    let x = ((e.clientX - rect.left) / rect.width) * 100;
    let y = ((e.clientY - rect.top) / rect.height) * 100;
    
    // 边界检测，确保值在0-100之间
    x = Math.max(0, Math.min(100, x));
    y = Math.max(0, Math.min(100, y));
    
    // 平滑过渡：如果距离上次位置变化太大，可以进行插值
    if (zoomPosition.x !== null && zoomPosition.y !== null) {
      const smoothFactor = 0.7; // 值越小，移动越平滑
      x = zoomPosition.x + (x - zoomPosition.x) * smoothFactor;
      y = zoomPosition.y + (y - zoomPosition.y) * smoothFactor;
    }
    
    // 更新放大位置状态
    setZoomPosition({ x, y });
    
    // 确保放大镜效果可见
    setIsZooming(true);
  };
  
  // 计算放大镜位置和放大预览的位置
  const calculateZoomStyles = () => {
    // 如果zoomPosition为null，使用默认值50%（居中）
    const xPos = zoomPosition.x !== null ? zoomPosition.x : 50;
    const yPos = zoomPosition.y !== null ? zoomPosition.y : 50;
    
    // 放大镜圆圈样式
    const magnifierStyle = {
      left: `${xPos}%`, 
      top: `${yPos}%`,
      transform: 'translate(-50%, -50%)'
    };
    
    // 放大预览图片样式
    const zoomFactor = 3; // 放大倍数
    
    // 计算放大图片的位置
    const zoomImageStyle = {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      transform: `scale(${zoomFactor})`,
      transformOrigin: `${xPos}% ${yPos}%`,
      transition: 'transform 0.1s ease-out'
    };
    
    return { magnifierStyle, zoomImageStyle };
  };
  

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const currentProductId = parseInt(productId);
        
        // 获取商品详情
        const currentProduct = await getProductById(currentProductId);
        setProduct(currentProduct);
        
        // 获取相关商品
        if (currentProduct) {
          const related = await getRelatedProducts(currentProductId);
          setRelatedProducts(related);
          
          // 检查收藏状态
          const favorites = await getFavorites();
          setIsFavorite(favorites.products.includes(currentProductId));
        }
        
      } catch (error) {
        console.error('获取商品详情失败:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [productId]);


  // 切换收藏状态
  const handleToggleFavorite = async () => {
    if (!product) return;
    
    try {
      const favorites = await toggleFavoriteProduct(product.id);
      setIsFavorite(favorites.products.includes(product.id));
    } catch (error) {
      console.error('切换收藏状态失败:', error);
    }
  };

  // 切换图片
  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-10 text-center">
        <p className="text-gray-500">加载中...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-10 text-center">
        <p className="text-gray-500">商品不存在</p>
        <Link to="/" className="text-primary hover:underline mt-4 inline-block">
          返回首页
        </Link>
      </div>
    );
  }

  // 确保产品有图片数组
  const productImages = product.images && product.images.length > 0 
    ? product.images 
    : [product.imageUrl];

  return (
    <div className="container mx-auto px-4 py-6">
      {/* 商品详情 */}
      <div className="bg-white bg-opacity-90 rounded-lg shadow-md overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6">
          {/* 图片展示区 */}
          <div>
            {/* 主图 */}
            <div className="mb-4 rounded-lg overflow-visible">{/* 修改为overflow-visible */}
              {productImages[currentImageIndex]?.endsWith('.mp4') ? (
                <video
                  src={productImages[currentImageIndex]}
                  alt={product.name}
                  className="w-full h-auto object-cover"
                  controls
                />
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  {/* 主图和放大镜区域 */}
                  <div className="relative zoom-container">
                    <img
                      ref={imageRef}
                      src={productImages[currentImageIndex] || productImages[0]}
                      alt={product.name}
                      className="w-[512px] h-[512px] object-cover zoom-image mx-auto"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      onMouseMove={handleMouseMove}
                      onClick={() => setShowOriginalPopup(true)}
                    />
                    
                    {/* 放大镜提示 */}
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-3 py-1 rounded-md text-xs pointer-events-none transition-opacity duration-300" style={{ opacity: isZooming ? 0 : 0.7 }}>
                      <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <span>鼠标悬停可放大查看</span>
                      </div>
                    </div>
                    
                    {/* 放大镜效果 */}
                    {isZooming && (
                      <> 
                        <div className="absolute inset-0 pointer-events-none">
                          <div 
                            className="absolute w-20 h-20 border-2 border-primary bg-white bg-opacity-10 rounded-full shadow-lg transition-transform duration-75 ease-out"
                            style={calculateZoomStyles().magnifierStyle}
                          >
                            <div className="absolute inset-0 border border-white border-opacity-40 rounded-full"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        
                        {/* 右侧放大预览区 */}
                        <div className="absolute left-full top-0 ml-4 zoom-preview w-96 h-96 border-2 border-primary bg-white rounded-lg overflow-hidden shadow-xl z-50 transition-opacity duration-200" style={{ opacity: 0.98 }}>
                          <div className="absolute top-0 left-0 right-0 bg-primary text-white text-center py-1 text-xs font-medium">
                            放大预览
                          </div>
                          <div className="absolute inset-0 pt-6 pb-2 px-2 overflow-hidden flex items-center justify-center">
                            <img 
                              src={productImages[currentImageIndex] || productImages[0]} 
                              alt="放大预览" 
                              className="w-full h-full object-contain" 
                              style={calculateZoomStyles().zoomImageStyle} 
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* 原图悬浮框 */}
            {showOriginalPopup && (
              <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
                <div className="relative bg-white rounded-lg shadow-2xl max-w-4xl max-h-[90vh]">
                  <button 
                    className="absolute top-4 right-4 bg-white bg-opacity-80 w-8 h-8 rounded-full flex items-center justify-center text-gray-700 hover:text-gray-900"
                    onClick={() => setShowOriginalPopup(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="flex items-center justify-center p-4">
                    <img 
                      src={productImages[currentImageIndex] || productImages[0]}
                      alt={`${product.name} 原图`}
                      className="max-h-[80vh] max-w-full object-contain"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 缩略图 */}
            <div className="flex space-x-2 overflow-x-auto pb-2">
              {productImages.map((image, index) => (
                <div
                  key={index}
                  className={`w-20 h-20 flex-shrink-0 rounded-md overflow-hidden cursor-pointer border-2 ${
                    index === currentImageIndex ? 'border-primary' : 'border-transparent'
                  }`}
                  onClick={() => handleImageClick(index)}
                >
                  <img
                    src={image.endsWith('.mp4') ? product.imageUrl : image}
                alt={`${product.name} - ${image.endsWith('.mp4') ? '视频' : '图片'} ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
          
          {/* 商品信息 */}
          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{product.name}</h1>
            
            <div className="flex items-center mb-4">
              <span className="bg-primary-light text-primary px-2 py-1 rounded-full text-sm">
                {product.crystalType}
              </span>
            </div>
            
            <p className="text-3xl font-bold text-primary mb-6">¥{product.price}</p>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">{product.description}</p>
            </div>
            
            {/* 操作按钮 */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={handleToggleFavorite}
                className={`flex-1 p-2 rounded-md border ${
                  isFavorite
                    ? 'bg-primary-light border-primary text-primary'
                    : 'border-gray-300 text-gray-600 hover:border-primary hover:text-primary'
                } transition-colors flex items-center justify-center`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 mr-2"
                  fill={isFavorite ? 'currentColor' : 'none'}
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={isFavorite ? 0 : 2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                {isFavorite ? '已收藏' : '收藏'}
              </button>
            </div>
          </div>
        </div>
        
        {/* 详细信息 */}
        <div className="border-t border-gray-200 p-6">
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">能量学说明</h2>
            <p className="text-gray-700">{product.energyDescription}</p>
          </div>
          
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">科学解释</h2>
            <p className="text-gray-700">{product.scienceExplanation}</p>
          </div>

          {/* 产品规格 */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">产品规格</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.material && (
                <div className="flex">
                  <span className="font-medium w-24 text-gray-700">材质:</span>
                  <span className="text-gray-600">{product.material}</span>
                </div>
              )}
              {product.size && (
                <div className="flex">
                  <span className="font-medium w-24 text-gray-700">尺寸:</span>
                  <span className="text-gray-600">{product.size}</span>
                </div>
              )}
              {product.weight && (
                <div className="flex">
                  <span className="font-medium w-24 text-gray-700">重量:</span>
                  <span className="text-gray-600">{product.weight}</span>
                </div>
              )}
              {product.origin && (
                <div className="flex">
                  <span className="font-medium w-24 text-gray-700">产地:</span>
                  <span className="text-gray-600">{product.origin}</span>
                </div>
              )}
              {product.certification && (
                <div className="flex">
                  <span className="font-medium w-24 text-gray-700">认证:</span>
                  <span className="text-gray-600">{product.certification}</span>
                </div>
              )}
            </div>
          </div>

          {/* 佩戴建议 */}
          {product.wearingAdvice && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">佩戴建议</h2>
              <p className="text-gray-700">{product.wearingAdvice}</p>
            </div>
          )}

          {/* 摆放建议 */}
          {product.placementAdvice && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">摆放建议</h2>
              <p className="text-gray-700">{product.placementAdvice}</p>
            </div>
          )}

          {/* 使用建议 */}
          {product.usageAdvice && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">使用建议</h2>
              <p className="text-gray-700">{product.usageAdvice}</p>
            </div>
          )}

          {/* 保养说明 */}
          {product.careInstructions && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">保养说明</h2>
              <p className="text-gray-700">{product.careInstructions}</p>
            </div>
          )}

          {/* 适用场景 */}
          {product.suitableFor && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">适用场景</h2>
              <p className="text-gray-700">{product.suitableFor}</p>
            </div>
          )}

          {/* 特殊特性 */}
          {product.specialFeatures && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">特殊特性</h2>
              <p className="text-gray-700">{product.specialFeatures}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* 相关商品 */}
      {relatedProducts.length > 0 && (
        <div className="mt-10">
          <h2 className="text-2xl font-bold mb-6 text-center">相关商品</h2>
          <ProductGrid products={relatedProducts} />
        </div>
      )}
    </div>
  );
};

export default ProductDetail;